// Receipt generation utilities for BeraStays withdrawals
import { format } from "date-fns";
import { getDateLocale } from "@/lib/dateUtils";

import i18n from "@/lib/i18n";
import { intlLocaleFor } from "@/lib/languageDetection";

const dt = (key: string, fallback: string) => i18n.t(key, { defaultValue: fallback }) as string;
export interface ReceiptData {
  id: string;
  date: string;
  amount: number;
  currency: string;
  ownerName: string;
  ownerIban: string;
  reason: string;
  status: string;
  processedAt?: string | null;
  company: {
    name: string;
    nif: string;
    address: string;
    phone: string;
    email: string;
    ambassador_message: string;
  };
}

function buildReceiptHTML(data: ReceiptData): string {
  const dateFormatted = format(new Date(data.date), dt("doc.receipt.longDateFormat", "dd 'de' MMMM 'de' yyyy"), { locale: getDateLocale() });
  const processedDate = data.processedAt
    ? format(new Date(data.processedAt), "dd/MM/yyyy · HH:mm", { locale: getDateLocale() })
    : "—";

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${dt("doc.receipt.title", "Recibo BeraStays")}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',sans-serif;background:#f8f9fb;color:#1a1a2e;padding:40px}
.receipt{max-width:560px;margin:0 auto;background:#fff;border-radius:20px;overflow:hidden;box-shadow:0 4px 40px rgba(0,0,0,0.06)}
.header{background:linear-gradient(135deg,#1a1a2e 0%,#2d2d4a 100%);color:#fff;padding:32px 36px;position:relative;overflow:hidden}
.header::after{content:'';position:absolute;top:-40px;right:-40px;width:120px;height:120px;background:rgba(212,168,83,0.08);border-radius:50%}
.header::before{content:'';position:absolute;bottom:-30px;left:-20px;width:80px;height:80px;background:rgba(212,168,83,0.05);border-radius:50%}
.brand{font-size:22px;font-weight:800;letter-spacing:-0.5px}
.brand span{color:#D4A853}
.subtitle{font-size:10px;color:rgba(255,255,255,0.5);margin-top:4px;letter-spacing:2px;text-transform:uppercase}
.receipt-label{position:absolute;top:32px;right:36px;background:rgba(212,168,83,0.15);color:#D4A853;padding:6px 14px;border-radius:8px;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase}
.body{padding:32px 36px}
.meta{display:flex;justify-content:space-between;margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid #f0f0f5}
.meta-item{font-size:11px;color:#8b8b9e}
.meta-value{font-size:13px;font-weight:600;color:#1a1a2e;margin-top:2px}
.amount-box{background:linear-gradient(135deg,#f0fdf4 0%,#ecfdf5 100%);border:1px solid #bbf7d0;border-radius:16px;padding:24px;text-align:center;margin-bottom:28px}
.amount-label{font-size:10px;color:#059669;text-transform:uppercase;letter-spacing:2px;font-weight:700}
.amount-value{font-size:36px;font-weight:800;color:#047857;margin-top:6px;letter-spacing:-1px}
.amount-unit{font-size:16px;font-weight:500;color:#6ee7b7}
.details{margin-bottom:28px}
.detail-row{display:flex;justify-content:space-between;padding:12px 0;border-bottom:1px solid #f5f5fa}
.detail-row:last-child{border-bottom:none}
.detail-label{font-size:12px;color:#8b8b9e;font-weight:500}
.detail-value{font-size:12px;color:#1a1a2e;font-weight:600;text-align:right;max-width:60%}
.status-badge{display:inline-flex;align-items:center;gap:4px;padding:4px 12px;border-radius:6px;font-size:11px;font-weight:700}
.status-completed{background:#ecfdf5;color:#059669}
.status-processing{background:#fffbeb;color:#d97706}
.status-rejected{background:#fef2f2;color:#dc2626}
.footer{background:#f8f9fb;padding:24px 36px;border-top:1px solid #f0f0f5}
.footer-company{font-size:11px;color:#8b8b9e;line-height:1.8}
.footer-company strong{color:#1a1a2e}
.ambassador{margin-top:16px;padding:16px;background:linear-gradient(135deg,#fef9ee 0%,#fdf6e3 100%);border:1px solid #fde68a;border-radius:12px;font-size:12px;color:#92400e;font-style:italic;text-align:center}
.watermark{text-align:center;padding:16px;font-size:9px;color:#ccc;letter-spacing:1px}
@media print{body{padding:0;background:#fff}.receipt{box-shadow:none;border-radius:0}}
</style>
</head><body>
<div class="receipt">
  <div class="header">
    <div class="brand">Bera<span>Stays</span></div>
    <div class="subtitle">${dt("doc.receipt.tagline", "Plataforma de Hospedagem Premium")}</div>
    <div class="receipt-label">${dt("doc.receipt.label", "Recibo")}</div>
  </div>
  <div class="body">
    <div class="meta">
      <div><div class="meta-item">${dt("doc.receipt.issueDate", "Data de Emissão")}</div><div class="meta-value">${dateFormatted}</div></div>
      <div style="text-align:right"><div class="meta-item">${dt("doc.receipt.reference", "Referência")}</div><div class="meta-value">#${data.id.slice(0, 8).toUpperCase()}</div></div>
    </div>
    <div class="amount-box">
      <div class="amount-label">${dt("doc.receipt.amount", "Valor do Pagamento")}</div>
      <div class="amount-value">${data.amount.toLocaleString(intlLocaleFor(i18n.language))} <span class="amount-unit">Kz</span></div>
    </div>
    <div class="details">
      <div class="detail-row"><span class="detail-label">${dt("doc.receipt.beneficiary", "Beneficiário")}</span><span class="detail-value">${data.ownerName}</span></div>
      <div class="detail-row"><span class="detail-label">IBAN</span><span class="detail-value" style="font-family:monospace;font-size:11px">${data.ownerIban}</span></div>
      <div class="detail-row"><span class="detail-label">${dt("doc.receipt.reason", "Motivo")}</span><span class="detail-value">${data.reason}</span></div>
      <div class="detail-row"><span class="detail-label">${dt("doc.receipt.status", "Estado")}</span><span class="detail-value"><span class="status-badge status-${data.status}">${data.status === "completed" ? `✓ ${dt("doc.receipt.statusPaid", "Pago com Sucesso")}` : data.status === "processing" ? `⏳ ${dt("doc.receipt.statusProcessing", "Em Processamento")}` : `✗ ${dt("doc.receipt.statusRejected", "Rejeitado")}`}</span></span></div>
      ${data.processedAt ? `<div class="detail-row"><span class="detail-label">${dt("doc.receipt.processedAt", "Data de Processamento")}</span><span class="detail-value">${processedDate}</span></div>` : ""}
    </div>
  </div>
  <div class="footer">
    <div class="footer-company">
      <strong>${data.company.name}</strong><br/>
      ${dt("doc.receipt.taxId", "NIF")}: ${data.company.nif}<br/>
      ${data.company.address}<br/>
      ${data.company.phone ? `${dt("doc.receipt.phone", "Tel")}: ${data.company.phone}<br/>` : ""}
      ${data.company.email}
    </div>
    ${data.company.ambassador_message ? `<div class="ambassador">"${data.company.ambassador_message}"</div>` : ""}
  </div>
  <div class="watermark">${dt("doc.receipt.autoGenerated", "Documento gerado automaticamente por")} ${data.company.name} · ${format(new Date(), "dd/MM/yyyy HH:mm")}</div>
</div>
</body></html>`;
}

import { htmlToPdfDownload } from "@/lib/htmlToPdf";

export async function downloadReceiptPDF(data: ReceiptData) {
  const html = buildReceiptHTML(data);
  await htmlToPdfDownload(html, `${dt("doc.receipt.fileName", "recibo")}-berastays-${data.id.slice(0, 8)}.pdf`, { width: 640 });
}

export async function downloadReceiptImage(data: ReceiptData) {
  await downloadReceiptPDF(data);
}
